"use client";

import { citiesInfo } from "@/info/cities";
import { usePathname } from "next/navigation";

interface UseCityResult {
	currentCity: string;
	isOnCityPage: boolean;
	isOnPreferencePage: boolean;
}

// List of supported cities - should match the ones in [city]/page.tsx
const SUPPORTED_CITIES = citiesInfo.map(a => a.code);
const DEFAULT_CITY = "bengaluru";

/**
 * Hook to get current city context from URL pathname
 * Returns the current city, whether user is on a city page, and whether on preference page
 */
export function useCurrentCity(): UseCityResult {
	const pathname = usePathname();

	// Check if we're on the preference page
	const isOnPreferencePage = pathname === "/preference";

	// Extract city from pathname like /bengaluru or /bengaluru/some-property
	let currentCity = DEFAULT_CITY;
	let isOnCityPage = false;

	if (pathname?.startsWith("/")) {
		const segments = pathname.split("/").filter(Boolean) || [];

		if (segments.length > 0) {
			const firstSegment = segments[0].toLowerCase();

			// Check if the first segment is a supported city
			if (SUPPORTED_CITIES.includes(firstSegment)) {
				currentCity = firstSegment;
				isOnCityPage = true;
			}
		}
	}

	return {
		currentCity,
		isOnCityPage,
		isOnPreferencePage,
	};
}

/**
 * Simple hook that just returns the current city string
 * Useful when you only need the city name for navigation
 */
export function useCurrentCityName(): string {
	const { currentCity } = useCurrentCity();
	return currentCity;
}
